import { useEffect, useId, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { X } from "lucide-react";
import { TOUCH_SQUARE } from "@/components/buttonStyles";
import { BidResultForm } from "@/components/BidResultForm";
import { WonBidStepperDialog } from "@/components/bond-submission/WonBidStepperDialog";
import type { BondSubmission } from "@/lib/brokerBonds";

// BRK-S422 — the Bid Result dialog the submission page's Bid Result button
// opens: the wireframe's step 0, "The request" and "How did this bid come
// out?", over `BidResultForm` — the bid result screen's own summary and
// question, so the 10 % rule (D7), the Update affordance
// (`bidResultLockReason`) and the server-confirmed close-out are one
// implementation on both surfaces.
//
// WON saves nothing here: it opens the won-bid stepper (BRK-S421) in this
// dialog's place, where the win is recorded. LOST records the loss here
// (`PUT …/bid-result`); the dialog says it is recorded only from the 2xx,
// and the close-out only from the read-back's `closed` / `not_awarded`.
//
// The button opens it only while `actions.bidResult` is enabled, so the form
// is always offered here; the reminder's `?focus=bid-result` links keep
// redirecting to the screen (BRK-S183).

export function BidResultDialog({
  order,
  open,
  onClose,
  onUpdated,
}: {
  order: BondSubmission;
  open: boolean;
  onClose: () => void;
  /** The order a 2xx answered with: the page shows it. */
  onUpdated: (order: BondSubmission) => void;
}) {
  const titleId = useId();
  const subId = useId();
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });
  // Won swaps this dialog for the stepper; closing either closes both.
  const [won, setWon] = useState(false);
  useEffect(() => {
    if (!open) setWon(false);
  }, [open]);
  return (
    <>
      <Dialog
        open={open && !won}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        fullScreen={phone}
        scroll="paper"
        // "Bid result SB-##### · {insured}"
        aria-labelledby={`${titleId} ${subId}`}
      >
        <div className="flex items-start gap-3 border-b border-brk-border p-4">
          <div className="flex min-w-0 flex-1 flex-col">
            <h2 id={titleId} className="text-heading text-brk-text">
              Bid result
            </h2>
            <p id={subId} className="break-words text-sm text-brk-dim">
              {[order.number, order.accountLegalName]
                .filter((part): part is string => !!part)
                .join(" · ")}
            </p>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className={`rounded-md p-2 text-brk-dim hover:bg-brk-bg hover:text-brk-text ${TOUCH_SQUARE}`}
          >
            <X aria-hidden="true" className="h-4 w-4" />
          </button>
        </div>
        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          <div className="flex min-w-0 flex-col gap-4">
            <BidResultForm
              order={order}
              onUpdated={onUpdated}
              offered
              headingLevel={3}
              onWon={() => setWon(true)}
            />
          </div>
        </DialogContent>
      </Dialog>
      <WonBidStepperDialog
        order={order}
        open={open && won}
        onClose={onClose}
        onUpdated={onUpdated}
      />
    </>
  );
}
